import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const clientId   = process.env.REACT_APP_COGNITO_CLIENT_ID    || '';

// When Cognito env vars are absent (local dev) auth is bypassed entirely.
const cognitoEnabled = Boolean(userPoolId && clientId);

const userPool = cognitoEnabled
  ? new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId })
  : null;

// Held across signIn → completeNewPassword calls
let pendingChallengeUser: CognitoUser | null = null;

export const CognitoService = {
  isEnabled(): boolean {
    return cognitoEnabled;
  },

  signIn(username: string, password: string): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      if (!userPool) return reject(new Error('Cognito not configured'));
      const user    = new CognitoUser({ Username: username, Pool: userPool });
      const details = new AuthenticationDetails({ Username: username, Password: password });
      user.authenticateUser(details, {
        onSuccess: resolve,
        onFailure: reject,
        newPasswordRequired: () => {
          pendingChallengeUser = user;
          reject(new Error('NewPasswordRequired'));
        },
      });
    });
  },

  completeNewPassword(newPassword: string): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      if (!pendingChallengeUser) return reject(new Error('No pending password challenge'));
      pendingChallengeUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session) => {
          pendingChallengeUser = null;
          resolve(session);
        },
        onFailure: reject,
      });
    });
  },

  forgotPassword(username: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!userPool) return reject(new Error('Cognito not configured'));
      const user = new CognitoUser({ Username: username, Pool: userPool });
      user.forgotPassword({
        onSuccess:             () => resolve(),
        onFailure:             reject,
        inputVerificationCode: () => resolve(),
      });
    });
  },

  confirmForgotPassword(username: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!userPool) return reject(new Error('Cognito not configured'));
      const user = new CognitoUser({ Username: username, Pool: userPool });
      user.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: reject,
      });
    });
  },

  signOut(): void {
    userPool?.getCurrentUser()?.signOut();
  },

  // Returns a valid access token JWT, refreshing the session if needed.
  // Returns null when no session exists.
  getAccessToken(): Promise<string | null> {
    return new Promise(resolve => {
      if (!userPool) return resolve(null);
      const user = userPool.getCurrentUser();
      if (!user) return resolve(null);
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) resolve(null);
        else resolve(session.getAccessToken().getJwtToken());
      });
    });
  },

  // Returns the email address of the currently logged-in user from the ID token.
  // Returns null when Cognito is not configured (local dev) or no session exists.
  getCurrentUserEmail(): Promise<string | null> {
    return new Promise(resolve => {
      if (!userPool) return resolve(null);
      const user = userPool.getCurrentUser();
      if (!user) return resolve(null);
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) return resolve(null);
        resolve(session.getIdToken().decodePayload()['email'] ?? null);
      });
    });
  },

  // Synchronous check for whether a user session exists in localStorage.
  // Does not validate token expiry — use getAccessToken() for that.
  hasCurrentUser(): boolean {
    if (!cognitoEnabled) return true;
    return userPool?.getCurrentUser() !== null;
  },
};
